import { cn } from '@/lib/utils';
import { Check, Eye, EyeOff, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Translator, { useTranslation } from 'components/i18n/Translator';

import Alert from './Alert';

interface Props {
  token: string;
  onResetPassword?: (token: string, newPassword: string) => Promise<any>;
  onSuccess?: () => void;
}

interface FormValues {
  password: string;
  confirmPassword: string;
}

interface PasswordStrength {
  minLength: boolean;
  uppercase: boolean;
  lowercase: boolean;
  number: boolean;
  special: boolean;
}

const ProgressIndicator = ({
  step,
  totalSteps
}: {
  step: number;
  totalSteps: number;
}) => (
  <div className="flex items-center justify-center mb-6">
    <div className="flex items-center space-x-2">
      {Array.from({ length: totalSteps }, (_, i) => (
        <div key={i} className="flex items-center">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              i < step
                ? 'bg-blue-600 text-white'
                : i === step
                ? 'bg-blue-100 text-blue-600 border-2 border-blue-600'
                : 'bg-gray-200 text-gray-400'
            }`}
          >
            {i + 1}
          </div>
          {i < totalSteps - 1 && (
            <div
              className={`w-12 h-0.5 mx-2 ${
                i < step ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  </div>
);

export function ResetPasswordForm({
  token,
  onResetPassword,
  onSuccess
}: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [expired, setExpired] = useState(false);

  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    minLength: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, touchedFields }
  } = useForm<FormValues>({
    defaultValues: {
      password: '',
      confirmPassword: ''
    }
  });

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  // Update password strength indicators
  useEffect(() => {
    if (password) {
      setPasswordStrength({
        minLength: password.length >= 12,
        uppercase: /[A-Z]/.test(password),
        lowercase: /[a-z]/.test(password),
        number: /\d/.test(password),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
      });
    }
  }, [password]);

  const onSubmit = async (data: FormValues) => {
    if (!onResetPassword) return;

    // Validate password confirmation
    if (data.password !== data.confirmPassword) {
      setError(t('auth.forgotPassword.form.confirmPassword.mismatch'));
      return;
    }

    // Check password strength
    const allStrengthChecks = Object.values(passwordStrength).every(Boolean);
    if (!allStrengthChecks) {
      setError(t('auth.forgotPassword.errors.passwordRequirements'));
      return;
    }

    setLoading(true);
    setError('');
    setExpired(false);

    try {
      const response = await onResetPassword(token, data.password);

      if (response.success) {
        setSuccess(true);
        if (onSuccess) {
          onSuccess();
        }
      } else {
        if (response.expired) {
          setExpired(true);
          setError(response.message || 'Reset link has expired');
        } else {
          setError(response.message || 'Password reset failed');
        }
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Password reset failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const StrengthIndicator = ({
    check,
    label
  }: {
    check: boolean;
    label: string;
  }) => (
    <div
      className={cn(
        'flex items-center gap-2 text-sm',
        check ? 'text-green-600' : 'text-gray-400'
      )}
    >
      {check ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
      <span>{label}</span>
    </div>
  );

  if (success) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="text-2xl font-bold">Password Reset Successful</h1>
        </div>

        <Alert variant="success">
          <div className="text-center">
            <p className="font-medium mb-2">Success!</p>
            <p className="text-sm">
              Your password has been reset successfully. You can now sign in
              with your new password.
            </p>
          </div>
        </Alert>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <h1 className="text-2xl font-bold">Reset Link Expired</h1>
        </div>

        <Alert variant="error">
          <div className="text-center">
            <p className="font-medium mb-2">Link Expired</p>
            <p className="text-sm">
              This password reset link has expired. Please request a new
              password reset.
            </p>
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={cn('flex flex-col gap-6')}
    >
      <ProgressIndicator step={2} totalSteps={3} />
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">
          <Translator path="auth.resetPassword.title" />
        </h1>
        <p className="text-muted-foreground">
          <Translator path="auth.resetPassword.description" />
        </p>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="grid gap-6">
        {/* New Password Field */}
        <div className="grid gap-2">
          <Label htmlFor="password">
            <Translator path="auth.resetPassword.form.password.label" />
          </Label>
          <div className="relative">
            <Input
              id="password"
              disabled={loading}
              type={showPassword ? 'text' : 'password'}
              placeholder={t('auth.resetPassword.form.password.placeholder')}
              {...register('password', {
                required: 'Password is required'
              })}
              className={cn(
                touchedFields.password &&
                  errors.password &&
                  'border-black dark:border-white'
              )}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* Password Strength Indicators */}
          {password && (
            <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg">
              <p className="text-sm font-medium mb-2">
                <Translator path="auth.forgotPassword.passwordRequirements.title" />
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                <StrengthIndicator
                  check={passwordStrength.minLength}
                  label={t(
                    'auth.forgotPassword.passwordRequirements.minLength'
                  )}
                />
                <StrengthIndicator
                  check={passwordStrength.uppercase}
                  label={t(
                    'auth.forgotPassword.passwordRequirements.uppercase'
                  )}
                />
                <StrengthIndicator
                  check={passwordStrength.lowercase}
                  label={t(
                    'auth.forgotPassword.passwordRequirements.lowercase'
                  )}
                />
                <StrengthIndicator
                  check={passwordStrength.number}
                  label={t('auth.forgotPassword.passwordRequirements.number')}
                />
                <StrengthIndicator
                  check={passwordStrength.special}
                  label={t('auth.forgotPassword.passwordRequirements.special')}
                />
              </div>
            </div>
          )}

          {touchedFields.password && errors.password && (
            <p className="text-sm text-destructive">
              {errors.password.message}
            </p>
          )}
        </div>

        {/* Confirm Password Field */}
        <div className="grid gap-2">
          <Label htmlFor="confirmPassword">
            <Translator path="auth.resetPassword.form.confirmPassword.label" />
          </Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              disabled={loading}
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder={t(
                'auth.resetPassword.form.confirmPassword.placeholder'
              )}
              {...register('confirmPassword', {
                required: t('auth.resetPassword.form.confirmPassword.required')
              })}
              className={cn(
                touchedFields.confirmPassword &&
                  (errors.confirmPassword ||
                    (confirmPassword && password !== confirmPassword)) &&
                  'border-black dark:border-white'
              )}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          </div>

          {confirmPassword && password !== confirmPassword && (
            <p className="text-sm text-destructive">Passwords do not match</p>
          )}

          {touchedFields.confirmPassword && errors.confirmPassword && (
            <p className="text-sm text-destructive">
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={
            loading ||
            !Object.values(passwordStrength).every(Boolean) ||
            password !== confirmPassword
          }
        >
          {loading
            ? t('auth.resetPassword.actions.resetting')
            : t('auth.resetPassword.actions.reset')}
        </Button>
      </div>
    </form>
  );
}
